import {
  useRuttingDistribution,
  useRuttingStats,
} from '@src/modules/performance/context/reports-api-hooks';
import { Text, VStack, HStack } from '@chakra-ui/react';
import { CHART_COLOURS } from '@src/modules/performance/shared/constants';
import ToggleableBarChart from '@src/modules/performance/components/charts/ToggleableBarChart';
import StatCard from '@src/modules/performance/components/charts/StatCard';
import ReportSection from './ReportSection';

const informationSource = {
  title: 'CCDC Rutting',
  content: [
    {
      title: null,
      description:
  <>
    <Text fontSize="inherit" mb={2}>
      Rutting is the depression of the pavement surface underneath the vehicle wheel paths.
    </Text>
    <Text fontSize="inherit" mb={2}>
      All percentages are based on the latest CCDC data recorded against each 10m road section in the network at the end of each financial year.
    </Text>
  </>,
      source: null,
    },
    {
      title: '% Rutting > 10mm',
      description:
  <>
    <Text fontSize="inherit" mb={2}>
      The percentage of the network that has rutting that is more than 10mm deep.
    </Text>
    <Text fontSize="inherit" mb={2}>
      The percentage is calculated as the sum of the left and right wheel path metres that have a rutting depth more than 10mm, compared to the total left and right wheel path metres.
    </Text>
  </>,
      source: null,
    },
    {
      title: '% Rutting > 20mm',
      description:
  <>
    <Text fontSize="inherit" mb={2}>
      The percentage of the network that has rutting that is more than 20mm deep.
    </Text>
    <Text fontSize="inherit" mb={2}>
      The percentage is calculated as the sum of the left and right wheel path metres that have a rutting depth more than 20mm, compared to the total left and right wheel path metres.
    </Text>
  </>,
      source: null,
    },
    {
      title: 'Annual distribution of rutting ',
      description:
  <>
    <Text fontSize="inherit" mb={2}>
      The chart shows the annual comparison of the percentage of the CCDC surveyed network that is in each rutting band.
    </Text>
    <Text fontSize="inherit" mb={2}>
      The rutting results are taken from left and right wheelpath lengths calculated within the rut depth bands. The bands are:
      <br />
      0-9mm deep, 10-19mm, 20-29mm, and 30mm+, for each 10m length of road surveyed.
    </Text>
    <Text fontSize="inherit" mb={2}>
      The results are calculated for the network length covered by CCDC surveys.
    </Text>
    <Text fontSize="inherit" mb={2}>
      There is no CCDC data available for State Highways.
    </Text>
  </>,
      source:
  <Text fontSize="inherit" as="span">
    Sourced from the Automated High-Speed Data collated through the CCDC programme
  </Text>,
    },
  ],
};

const CHART_CATEGORIES = [
  { label: '0-9mm', key: 'rut0To9MmPerc' },
  { label: '10-19mm', key: 'rut10To19MmPerc' },
  { label: '20-29mm', key: 'rut20To29MmPerc' },
  { label: '30mm+', key: 'rut30MmPlusPerc' },
];

function RoadConditionRutting() {
  const { data: ruttingStats, isLoading: isLoadingRuttingStats } = useRuttingStats();
  const { data: ruttingDistribution, isLoading: isLoadingRuttingDistribution } = useRuttingDistribution();
  const isLoading = isLoadingRuttingStats || isLoadingRuttingDistribution;

  return (
    <ReportSection
      title="CCDC Rutting"
      isLoading={isLoading || !ruttingStats || !ruttingDistribution}
      lockYear={ruttingStats?.quadLockYear}
      informationSource={informationSource}
    >
      <VStack spacing={6}>
        <HStack spacing={2} width="100%">
          <StatCard
            value={ruttingStats?.rut10MmPlusPerc}
            label="Rutting > 10mm"
            suffix="%"
          />
          <StatCard
            value={ruttingStats?.rut20MmPlusPerc}
            label="Rutting > 20mm"
            suffix="%"
          />
        </HStack>
        <ToggleableBarChart
          data={ruttingDistribution || []}
          categories={CHART_CATEGORIES}
          seriesKey="quadLockYear"
          chartTitle="Annual distribution of rutting"
          yAxisLabel="Percentage"
          colors={[
            CHART_COLOURS.blue,
            CHART_COLOURS.red,
            CHART_COLOURS.green,
            CHART_COLOURS.purple,
            CHART_COLOURS.orange,
            CHART_COLOURS.yellow,
            CHART_COLOURS.magenta,
          ]}
        />
      </VStack>
    </ReportSection>
  );
}

export default RoadConditionRutting;
